import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCustom, useCustomMutation, useTranslate } from '@refinedev/core';
import { Alert, Button, Modal, Space, Tooltip, Typography, message } from 'antd';
import { useCallback, useState } from 'react';

const { Text, Title } = Typography;

type PermanentInviteLink = {
  practiceId: string;
  token: string;
  url: string;
  issuedAt: string;
};

/**
 * E07 · the PRACTICE'S REVOCABLE CLIENT-INVITE LINK (M7, board cc-rtm; SDL `be/sharing.gql` §3).
 *
 * ─── WHAT IT REPLACES, AND WHY THAT HAD TO GO ──────────────────────────────────────────────────
 * The block that stood here rendered `copingcard.com/deep/join/users/<userKey>` — built in the
 * browser out of the signed-in therapist's own row id. It was never a token:
 *
 *   • it could not be revoked, so a practitioner who left kept a working link for life;
 *   • it named a ROW, so editing the number in the URL reached ANOTHER PRACTICE (the founder's own
 *     finding, 2026-07-20);
 *   • it was per-THERAPIST, while the thing a practice wants to hand out is per-PRACTICE.
 *
 * The replacement is an opaque CSPRNG token the SERVER resolves to a practice. A guessed, mutated
 * or rotated token matches nothing, and `InviteType.PERMANENT_LINK_CLIENT` — in the enum since the
 * beginning with no resolver ever producing or consuming it — finally means something.
 *
 * ─── FIRST USE MINTS, REGENERATE ROTATES ───────────────────────────────────────────────────────
 * `permanentInviteLink` answers null for a practice that has never had one, so the first render
 * offers Generate rather than showing an empty box. Regenerate calls `rotatePermanentInviteLink`,
 * and the ONLY honest thing to say about it is that every link already sent stops working —
 * confirmed behind a modal that says exactly that, because a practice with printed leaflets needs
 * to know before the tap, not after.
 *
 * ─── WHO SEES IT ───────────────────────────────────────────────────────────────────────────────
 * Both operations are THERAPIST_ADMIN-only server-side. A non-admin gets a null link and the
 * explanatory line rather than a Generate button that would refuse them.
 */
export const PermanentInviteLinkBlock = () => {
  const translate = useTranslate();
  const [rotating, setRotating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isFetching, refetch } = useCustom<{
    permanentInviteLink: PermanentInviteLink | null;
  }>({
    url: '',
    method: 'get',
    meta: {
      operation: 'permanentInviteLink',
      fields: ['practiceId', 'token', 'url', 'issuedAt'],
    },
  });

  const link = ((data as any)?.data ?? null) as PermanentInviteLink | null;

  const { mutateAsync: runMutation } = useCustomMutation();

  const run = useCallback(
    async (operation: 'generatePermanentInviteLink' | 'rotatePermanentInviteLink') => {
      setBusy(true);
      setFailure(null);

      try {
        await runMutation({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: ['practiceId', 'token', 'url', 'issuedAt'],
            variables: {},
          },
        });

        await refetch();
        setRotating(false);
      } catch (error) {
        setFailure(
          (error as { message?: string })?.message
          || translate('portalInvites.permanentLink.failed'),
        );
      } finally {
        setBusy(false);
      }
    },
    [refetch, runMutation, translate],
  );

  const copy = useCallback(() => {
    if (!link?.url) {
      return;
    }

    navigator.clipboard
      .writeText(link.url)
      .then(() => message.success(translate('portalInvites.permanentLink.copied')))
      .catch(() => undefined);
  }, [link?.url, translate]);

  return (
    <div
      style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}
      data-testid="cc-permanent-invite-link"
    >
      <Title style={{ marginRight: 10 }} level={5}>
        {translate('portalInvites.permanentLink.title')}:
      </Title>

      <Space direction="vertical" size="small">
        <Space wrap align="center">
          {link?.url ? (
            <>
              <Text
                copyable={false}
                style={{ marginRight: 15 }}
                data-testid="cc-permanent-invite-link-url"
              >
                {link.url}
              </Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                title={translate('invites.fields.copy')}
                onClick={copy}
                data-testid="cc-permanent-invite-link-copy"
              >
                {translate('invites.fields.copy')}
              </Button>
              <Tooltip title={translate('portalInvites.permanentLink.regenerateTip')}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={busy}
                  onClick={() => setRotating(true)}
                  data-testid="cc-permanent-invite-link-regenerate"
                >
                  {translate('portalInvites.permanentLink.regenerate')}
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              <Text type="secondary">
                {translate('portalInvites.permanentLink.none')}
              </Text>
              <Button
                size="small"
                loading={busy || isFetching}
                onClick={() => {
                  run('generatePermanentInviteLink').catch(() => undefined);
                }}
                data-testid="cc-permanent-invite-link-generate"
              >
                {translate('portalInvites.permanentLink.generate')}
              </Button>
            </>
          )}
        </Space>

        {failure ? <Alert type="error" showIcon message={failure} /> : null}
      </Space>

      <Modal
        open={rotating}
        title={translate('portalInvites.permanentLink.regenerateTitle')}
        okText={translate('portalInvites.permanentLink.regenerate')}
        cancelText={translate('buttons.cancel')}
        confirmLoading={busy}
        okButtonProps={{ 'data-testid': 'cc-permanent-invite-link-regenerate-confirm' }}
        onOk={() => {
          run('rotatePermanentInviteLink').catch(() => undefined);
        }}
        onCancel={() => setRotating(false)}
      >
        <Text>{translate('portalInvites.permanentLink.regenerateWarning')}</Text>
      </Modal>
    </div>
  );
};
